<template>
  <div class="container py-5">

    <div class="columns hide-smallscreen flex flex--row flex--align-end">
      <div class="column">
        <label class="label">Search by Project</label>
        <search-autocomplete 
          :value="filters.project_id"
          @input="changeFilter('project_id', $event)"
          @updateProject="setProject($event)"/>
      </div>
      <div class="column">
        <label class="label">Search by Skid/ Group #</label>
        <v-search 
          placeholder="Search by Skid/ Group #"
          :search-input.sync="filters.skid_no"
          @search="changeFilter('skid_no', $event)"
          @clear="changeFilter('skid_no', null)"/>
      </div>
      <div class="column">
        <label class="label">Search by Piece #</label>
        <v-search 
          placeholder="Search by Piece #"
          :search-input.sync="filters.piece_no"
          @search="changeFilter('piece_no', $event)"
          @clear="changeFilter('piece_no', null)"/>
      </div>
      <button
        @click="download" 
        class="button mb-3 mr-3">
        <span class="icon">
          <i class="fas fa-download"></i>
        </span>
      </button>
    </div>

    <div class="hide-largescreen px-3">
      <div class="flex flex--row flex--justify-space-between">
        <label class="label">
          Filters
        </label>

        <button
          @click="download" 
          class="button is-small">
          <span class="icon">
            <i class="fas fa-download"></i>
          </span>
        </button>
      </div>

      <div class="field is-grouped is-grouped-multiline">
        <div 
          v-for="(filterValue, filterName) in activeFilters" 
          :key="filterName"
          class="control">
          <div 
            class="tags has-addons">
            <span class="tag is-primary">
              <span class="pr-1">{{ filterNameMap[filterName] }}:</span>
              <span v-if="filterName != 'project_id'"> {{ filterValue }}</span>
              <span v-if="filterName == 'project_id' && project"> {{ project.project_name }}</span>
            </span>
            <a 
              @click="changeFilter(filterName, null)" 
              class="tag is-delete">
            </a>
          </div>
        </div>
      </div>

      <button
        @click="addingFilters = true" 
        class="button">
        Add Filters
      </button>
    </div>

    <hr/>

    <v-alert 
      class="my-3"
      v-if="!loading && installationItems.length == 0"
      type="info">
      <span v-if="numberOfFilters == 0">
        There are no items.
      </span>
      <span v-else>
        There are no items with the parameters used.
      </span> 
    </v-alert>

    <table v-if="installationItems.length > 0" 
      class="table is-fullwidth is-responsive">
      <thead>
        <tr>
          <th 
            v-for="(header, i) in headers"
            :key="i">
            {{ header }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr 
          v-for="installationItem in installationItems"
          :key="installationItem.id">
          <td data-title="Project Name">
            {{ installationItem.installation_group.project.project_name }}
          </td>
          <td data-title="Skid/ Group #">
            {{ installationItem.installation_group.skid_no }}
          </td>
          <td data-title="Piece #">
            {{ installationItem.piece_no }}
          </td>
          <td data-title="Description">
            {{ installationItem.desc }}
          </td>
          <td 
            :class="{ 'hide-smallscreen': !installationItem.status_note }"
            data-title="Status Notes">
            {{ installationItem.status_note }}
          </td>
          <td
            :class="{ 'hide-smallscreen': !installationItem.attachment }" 
            data-title="Image">
            <a 
              v-if="installationItem.attachment" 
              :href="installationItem.attachment"
              target="_blank">
              Image Attachment
            </a>
          </td>
          <td class="hide-smallscreen">
            <button 
              v-if="!installationItem.is_remediated" 
              @click="launchRemediationModal(installationItem)"
              class="button">
              Mark as Remediated
            </button>
            <span v-else>
              {{ format(parse(installationItem.remediated_on)) }}
            </span>
          </td>
          <td
            :class="{ 'hide-smallscreen': !installationItem.remediated_note }" 
            data-title="Remediation Notes">
            {{ installationItem.remediated_note }}
          </td>
          <td
            data-title="Remediated On" 
            class="hide-largescreen">
            <button 
              v-if="!installationItem.is_remediated" 
              @click="launchRemediationModal(installationItem)"
              class="button">
              Mark as Remediated
            </button>
            <span v-else>
              {{ format(parse(installationItem.remediated_on)) }}
            </span>
          </td>

        </tr>
      </tbody>
    </table>

    <v-confirm-modal 
      v-if="!firstLoad && confirmModal.item"
      v-model="confirmModal.state"
      :title="confirmModal.title"
      :type="confirmModal.type"
      @confirm="confirmModal.confirmedCb()"
      @cancel="confirmModal.state = false"
      :confirmText="confirmModal.confirmText"
      >
      <div class="field">
        <label class="label">Mark Date Remediated</label>
        <v-datetimepicker 
          v-model="confirmModal.remediated_on"/>
      </div>
      <div class="field">
        <label class="label">Remediation Notes</label>
        <v-text-area
          placeholder="Leave remediation details" 
          v-model="confirmModal.remediated_note"/>
      </div>
    </v-confirm-modal>

    <add-filters-modal 
      v-model="addingFilters"
      :show-filters="['project_id', 'skid_no', 'piece_no']"/>
  </div>
</template>

<script>
import VSearch from '@/components/base/VSearch'
import VConfirmModal from '@/components/base/VConfirmModal'
import VAlert from '@/components/base/VAlert'
import VDatetimepicker from '@/components/base/VDatetimepicker'
import VTextArea from '@/components/base/VTextArea'

import SearchAutocomplete from '@/components/projects/SearchAutocomplete'
import AddFiltersModal from '@/components/installationItems/AddFiltersModal'

import { downloadCsv } from '@/mixins/downloadable.js'

import { mapState, mapActions, mapMutations } from 'vuex'
import { parseISO, format } from 'date-fns'

export default {
  components: { 
    VSearch, VDatetimepicker, VConfirmModal, VAlert, VTextArea, 
    AddFiltersModal, SearchAutocomplete
  },
  mixins: [downloadCsv],
  data() {
    return {
      headers: [
        "Project", "Group/Skid #", "Piece #", "Desc", "Status Notes", "Image",
        "Remediation Date", "Remediation Notes"
      ],
      confirmModal: {
        state: false,
        title: "Enter Date Remediated",
        confirmText: "Mark as Remediated",
        type: "info",
        item: null,
        remediated_on: null,
        remediated_note: null,
        confirmedCb: v => v
      },
      filterNameMap: {
        "project_id": "Project", 
        "skid_no": "Skid/ Group #",
        "piece_no": "Piece #",
        "is_sent": "Sent Only",
        "no_status": "Uninstalled Only",
        "status": "Status"
      },
      addingFilters: false,
      parse: (d) => {
        return d ? parseISO(d) : null
      },
      format: (d) => {
        return d ? format(d, 'yyyy-MM-dd H:mm:ss') : null
      },
      formatCsvDate: (d) => {
        return d ? format(d, 'yyyy-MM-dd_H.mm.ss') : null
      }
    } 
  },
  computed: {
    ...mapState("installationItems", [
      "installationItems", "loading", "firstLoad", 
      "selected", "filters", "pagination", "project"
    ]),
    numberOfFilters({ filters }) {
      return Object.values(filters).filter(d => d != null && d != false).length
    },
    activeFilters({ filters }) {
      return Object.fromEntries(
        Object.entries(filters).filter(d => d[1] != null && d[1] != false && d[0] != 'status' && d[0] != 'is_sent')
      )
    }
  },
  watch: {
    "pagination.page"(newPage, oldPage) {
      let params = {...this.pagination, ...this.filters}
      this.getInstallationItems(params);
    },
    '$route.query': {
      handler(query) {
        this.getInstallationItems(query);
      },
      deep: true
    }
  },  
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("installationItems", [
      "getInstallationItems", "updateInstallationItem", "addAttachment"
    ]),
    ...mapMutations("installationItems", ["setFilter", "setProject"]),
    ...mapMutations("app", ["setNotification"]),
    initialize() {
      let query = this.$route.query;
      if (query) {
        Object.keys(query).forEach(key => {
          this.setFilter({ field: key, value: query[key] })
        })
        this.getInstallationItems(query);
      } else {
        this.getInstallationItems();
      }
    },
    changeFilter(field, value) {
      this.setFilter({ field: field, value: value });
      let query = Object.assign({}, this.$route.query);
      if (value) {  
        query[field] = value;
        this.$router.push({ name: 'InstallationDefectedItems', query: query })
      } else {
        delete query[field];
        this.$router.push({ name: 'InstallationDefectedItems', query: query })
      }
    },
    launchRemediationModal(installationItem) {
      const { confirmModal } = this;

      confirmModal.item = installationItem;
      confirmModal.state = true;
      confirmModal.confirmedCb = () => { 
        if (confirmModal.remediated_on) {
          this.saveRemediationDate();
          confirmModal.state = false;
        } else {
          this.setNotification({
            type: 'danger',
            status: true,
            message: `Please add remediation date.`
          });
        }
      }
    },
    saveRemediationDate() {
      const { confirmModal } = this;

      this.updateInstallationItem({ 
        installationItemId: confirmModal.item.id,
        data: {
          is_remediated: true,
          remediated_on: confirmModal.remediated_on,
          remediated_note: confirmModal.remediated_note
        }
      }).then(() => this.clearModal())
    },
    download() {
      this.downloadCsv({ 
        data: this.installationItems.map(d => {
          let res = {}
          res['Project Name'] = d.installation_group.project.project_name;
          res['Skid/ Group #'] = d.installation_group.skid_no;
          let otherFields = [
            { field: 'piece_no', label: 'Piece #'},  
            { field: 'desc', label: 'Description' },
            { field: 'status_note', label: 'Status Note' },
            { field: 'attachment', label: 'Image Attachment Link' },
            { field: 'is_remediated', label: 'Remediated' },
            { field: 'remediated_on', label: 'Remediated On', format: d => this.format(this.parse(d)) },
            { field: 'remediated_note', label: 'Remediation Note' }]
          
          otherFields.forEach(o => {
            res[o.label] = o.format? o.format(d[o.field]) : d[o.field];
          })

          return res
        }), 
        filename: `defected_items_${this.formatCsvDate(new Date)}.csv` 
      });
    },
    clearModal() {
      this.confirmModal.item = null;
      this.confirmModal.remediated_on = null;
      this.confirmModal.remediated_note = null;
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/styles/table.scss';
@import '@/styles/flexbox.scss';
@import '@/styles/responsive.scss';
</style>